import { actionTypes, change as reduxFormChange } from 'redux-form';

import { setStornoInsurancePrice, setTotalPrice, setPriceToPay, setTotalNoOfGuests, setNoOfNights, setApiDiscountCodeResponse, setDiscountCodeAutoRemoved } from './actions';
import { getBoardBaseTotalPrice, getBoardBasesTotalPrice, getExtraFeePrice, getRoomPrice, getNoOfNights, getTotalPricelistFees, getPackagePrice, getPackageAccommodationPrice, getRoomNoOfGuests, getRoomsNoOfGuests, isWithoutAccommodation } from '../apiHelpers';
import { getDiscountCodePrice, isDiscountCodeValid } from "./discountCodeSelector";

export default store => next => action => {
	// let the action go to the reducers
	next(action);

	if (action.type !== actionTypes.CHANGE) return;

	const state = store.getState();
	const formValues = state.form.bookingForm.values;
	const { rooms, currency, boardBase, stayType } = formValues;
	const { isAdvancePaymentRequired, advancePaymentPercentage, boardBases, applyGuestTypeDiscountToBoardBase, extraFees, guestTypes, stornoInsurance, apiRoomsResponse, packages, package: eventPackage } = state.app;

	// Formulář ještě není inicializovaný
	if (!currency || !rooms || !apiRoomsResponse) return;

	const pricelistFees = apiRoomsResponse.pricelistFees;

	let stornoInsurancePrice = 0;
	let totalPrice = 0;
	let priceToPay = 0;
	let totalNoOfGuests = 0;
	const noOfNights = getNoOfNights(formValues.arrival, formValues.departure);

	// Rezervace pouze kalendářové události bez ubytování: cena je cena balíčku, automatické příplatky/slevy navázané na
	// balíček a strava (bez cen ubytování, poplatků a pojištění)
	const withoutAccommodation = isWithoutAccommodation(formValues);

	// Přednastavený balíček/událost má jednu pevnou cenu za celou rezervaci bez ohledu na počet vybraných pokojů
	// - proto se jeho cena přičte jen jednou
	let eventPackagePriceCounted = false;

	for (let roomType of Object.keys(rooms)) {
		// Pro každý vybraný typ pokoje
		for (let selectedRoom of rooms[roomType]) {
			// Pro každý vybraný konkrétní pokoj
			totalNoOfGuests += Object.values(selectedRoom.guestTypes).reduce((accumulator, noOfGuests) => accumulator + noOfGuests, 0);

			// Pokud má pokoj vybraný pobytový balíček, cena balíčku nahrazuje cenu ubytování
			const selectedPackage = selectedRoom.package ? packages[selectedRoom.package] : null;
			const isEventPackage = !!(selectedPackage && eventPackage && selectedPackage.id === eventPackage.id);

			// cena ubytování (s balíčkem ji nahrazuje cena balíčku)
			let roomPrice;
			if (isEventPackage) {
				// cena za každého hosta platí za všechny hosty rezervace
				roomPrice = eventPackagePriceCounted ? 0 : getPackagePrice(selectedPackage, noOfNights, currency, false, getRoomsNoOfGuests(rooms));
				eventPackagePriceCounted = true;
			} else {
				roomPrice = selectedPackage
					? getPackagePrice(selectedPackage, noOfNights, currency, false, getRoomNoOfGuests(selectedRoom))
					: getRoomPrice(selectedRoom, currency, selectedRoom.guestTypes, guestTypes);
			}
			// samostatně nastavená cena ubytování u balíčku (nezávislá na "counted once" pravidlu balíčku - počítá se za každý pokoj)
			if (selectedPackage && !withoutAccommodation) {
				roomPrice += getPackageAccommodationPrice(selectedPackage, noOfNights, currency, false, getRoomNoOfGuests(selectedRoom));
			}
			totalPrice += roomPrice;

			// Automatické příplatky a slevy (s balíčkem se počítají jen ty, které jsou na daný balíček omezené)
			for (const extraFee of apiRoomsResponse.extraFees) {
				if (selectedPackage && !(extraFee.packageIds || []).includes(selectedPackage.id)) {
					continue;
				}

				if (typeof extraFee.price === 'object') {
					// Cena zadaná pevnou částkou
					if (extraFee.price[currency.id]) {
						totalPrice += extraFee.price[currency.id] * (extraFee.daily ? noOfNights : 1);
					}
				} else {
					// Cena zadaná procentem z ceny ubytování
					totalPrice += roomPrice * extraFee.price / 100;
				}
			}

			// Zvolené příplatky a slevy (počítají se i s balíčkem)
			if (selectedRoom.extraFees !== undefined) {
				for (const extraFeeId of Object.keys(selectedRoom.extraFees)) {
					if (selectedRoom.extraFees[extraFeeId]) {
						totalPrice += getExtraFeePrice(extraFees[extraFeeId], noOfNights, roomPrice, currency, false);
					}
				}
			}
		}
	}

	// Pojištění proti stornu (počítá se pouze z ceny za ubytování)
	if (stornoInsurance && !withoutAccommodation) {
		stornoInsurancePrice = totalPrice * stornoInsurance.percentageFee; // v totalPrice musí být pouze cena za ubytování (+ příplatky a slevy)
		if (formValues.stornoInsurance) {
			totalPrice += stornoInsurancePrice;
		}
	}

	// K celkové ceně přičíst poplatky
	if (!withoutAccommodation) {
		totalPrice += getTotalPricelistFees(pricelistFees, rooms, noOfNights, currency, stayType);
	}

	if (boardBase && boardBases) {
		// Připočítat stravu
		totalPrice += typeof boardBase === 'object'
			// Strava může být pro každoho hosta jiná
			? getBoardBasesTotalPrice(boardBases, boardBase, guestTypes, noOfNights, applyGuestTypeDiscountToBoardBase, formValues.currency, false)
			// Strava je pro všechny hosty stejná
			: getBoardBaseTotalPrice(boardBases[boardBase], guestTypes, rooms, noOfNights, applyGuestTypeDiscountToBoardBase, formValues.currency, false);
	}

	if (isDiscountCodeValid(state)) {
		const discountPrice = getDiscountCodePrice(state);
		if (discountPrice > totalPrice) {
			// Sleva přesahuje celkovou cenu – odebereme slevový kód
			store.dispatch(setApiDiscountCodeResponse(undefined));
			store.dispatch(reduxFormChange('bookingForm', 'discountCode', ''));
			store.dispatch(setDiscountCodeAutoRemoved(true));
		} else {
			totalPrice = totalPrice - discountPrice;
		}
	}

	if (isAdvancePaymentRequired || (advancePaymentPercentage < 100 && formValues.advancePaymentOnly)) {
		priceToPay = (totalPrice * advancePaymentPercentage) / 100;
	} else {
		priceToPay = totalPrice;
	}

	store.dispatch(setStornoInsurancePrice(stornoInsurancePrice));
	store.dispatch(setTotalPrice(totalPrice));
	store.dispatch(setPriceToPay(priceToPay));
	store.dispatch(setTotalNoOfGuests(totalNoOfGuests));
	store.dispatch(setNoOfNights(noOfNights))
};


// WEBPACK FOOTER //
// ./src/store/totalPriceMiddleware.js